/**
 * Write a bundled skill into an agent's expected location, formatted
 * for that agent's convention. Each agent ships skills slightly
 * differently — same content underneath, different wrapper.
 *
 * Version pinning: every installed file carries an HTML comment header
 * `<!-- clawnify-skill: <name> v<X.Y.Z> -->`. On reinstall we read that
 * header; if the version matches the CLI's current version, we skip
 * (no-op idempotency). If it differs, we overwrite — the user is
 * implicitly upgrading by re-running after a CLI bump.
 */

import {
  existsSync,
  mkdirSync,
  readFileSync,
  writeFileSync,
  statSync,
  rmSync,
  unlinkSync,
} from "node:fs";
import { dirname, join } from "node:path";
import { CLI_VERSION, type BundledSkill } from "./content.gen.js";
import type { DetectedAgent } from "./detect.js";

const VERSION_HEADER_RE = /<!--\s*clawnify-skill:\s*([\w-]+)\s+v([\d.]+)\s*-->/;

function versionHeader(name: string): string {
  return `<!-- clawnify-skill: ${name} v${CLI_VERSION} -->`;
}

function readExistingVersion(path: string): string | null {
  if (!existsSync(path)) return null;
  try {
    const head = readFileSync(path, "utf8").slice(0, 500);
    const match = head.match(VERSION_HEADER_RE);
    return match ? match[2] : null;
  } catch {
    return null;
  }
}

export type InstallStatus =
  | { status: "installed"; path: string }
  | { status: "updated"; path: string; previousVersion: string }
  | { status: "skipped"; path: string; reason: string };

export function installSkill(
  skill: BundledSkill,
  agent: DetectedAgent,
  cwd: string,
): InstallStatus {
  switch (agent.agent) {
    case "claude":
      return writeSkillDir(skill, cwd, agent.target, formatClaudeSkill);
    case "codex":
      return writeSkillDir(skill, cwd, agent.target, formatCodexSkill);
    case "openclaw":
      return writeOpenclawSkill(skill, cwd, agent.target);
    case "cursor":
      return writeCursorRule(skill, cwd, agent.target);
    case "aider":
      return writeAiderSection(skill, cwd, agent.target);
  }
}

// ── Claude Code / Codex: <target>/<name>/SKILL.md ─────────────────────
// Both follow the same convention: a skill directory containing
// SKILL.md. Frontmatter carries name + description so the agent's
// indexer picks it up.

function formatClaudeSkill(skill: BundledSkill): string {
  return `${versionHeader(skill.name)}
---
name: ${skill.name}
description: ${JSON.stringify(skill.description)}
---

${skill.content}`;
}

function formatCodexSkill(skill: BundledSkill): string {
  // Codex SKILL.md uses the same frontmatter shape as Claude — keeps the
  // wrapper consistent so the underlying doc stays portable.
  return formatClaudeSkill(skill);
}

function writeSkillDir(
  skill: BundledSkill,
  cwd: string,
  targetDir: string,
  format: (s: BundledSkill) => string,
): InstallStatus {
  const skillDir = join(cwd, targetDir, skill.name);
  const filePath = join(skillDir, "SKILL.md");
  const existing = readExistingVersion(filePath);
  if (existing === CLI_VERSION) {
    return { status: "skipped", path: filePath, reason: `already at v${CLI_VERSION}` };
  }
  mkdirSync(skillDir, { recursive: true });
  writeFileSync(filePath, format(skill));
  return existing
    ? { status: "updated", path: filePath, previousVersion: existing }
    : { status: "installed", path: filePath };
}

// ── OpenClaw: <target>/<name>/SKILL.md + skill.json ───────────────────

function writeOpenclawSkill(
  skill: BundledSkill,
  cwd: string,
  targetDir: string,
): InstallStatus {
  const skillDir = join(cwd, targetDir, skill.name);
  const mdPath = join(skillDir, "SKILL.md");
  const jsonPath = join(skillDir, "skill.json");
  const existing = readExistingVersion(mdPath);
  if (existing === CLI_VERSION) {
    return { status: "skipped", path: mdPath, reason: `already at v${CLI_VERSION}` };
  }
  mkdirSync(skillDir, { recursive: true });
  writeFileSync(mdPath, `${versionHeader(skill.name)}
${skill.content}`);
  writeFileSync(
    jsonPath,
    JSON.stringify(
      {
        name: skill.name,
        displayName: skill.name
          .split("-")
          .map((p) => p[0].toUpperCase() + p.slice(1))
          .join(" "),
        description: skill.description,
        trigger: "manual",
        inputs: [],
      },
      null,
      2,
    ),
  );
  return existing
    ? { status: "updated", path: mdPath, previousVersion: existing }
    : { status: "installed", path: mdPath };
}

// ── Cursor: .cursor/rules/<name>.mdc ──────────────────────────────────

function writeCursorRule(
  skill: BundledSkill,
  cwd: string,
  targetDir: string,
): InstallStatus {
  const dir = join(cwd, targetDir);
  const filePath = join(dir, `${skill.name}.mdc`);
  const existing = readExistingVersion(filePath);
  if (existing === CLI_VERSION) {
    return { status: "skipped", path: filePath, reason: `already at v${CLI_VERSION}` };
  }
  mkdirSync(dir, { recursive: true });
  const body = `${versionHeader(skill.name)}
---
description: ${JSON.stringify(skill.description)}
alwaysApply: false
---

${skill.content}`;
  writeFileSync(filePath, body);
  return existing
    ? { status: "updated", path: filePath, previousVersion: existing }
    : { status: "installed", path: filePath };
}

// ── Aider: append a section to CONVENTIONS.md ─────────────────────────
// Aider doesn't have a per-skill directory model — its convention is a
// single CONVENTIONS.md the user --reads into context. We append a
// marker-delimited section so reruns replace cleanly.

function writeAiderSection(
  skill: BundledSkill,
  cwd: string,
  targetFile: string,
): InstallStatus {
  const filePath = join(cwd, targetFile);
  const startMarker = `<!-- clawnify-skill-start: ${skill.name} -->`;
  const endMarker = `<!-- clawnify-skill-end: ${skill.name} -->`;
  const section = `${startMarker}
${versionHeader(skill.name)}
${skill.content}
${endMarker}
`;

  let existing = "";
  let existingVersion: string | null = null;
  if (existsSync(filePath)) {
    existing = readFileSync(filePath, "utf8");
    const sectionRe = new RegExp(
      `${escapeRe(startMarker)}[\\s\\S]*?${escapeRe(endMarker)}\\n?`,
    );
    const sectionMatch = existing.match(sectionRe);
    if (sectionMatch) {
      const headMatch = sectionMatch[0].match(VERSION_HEADER_RE);
      existingVersion = headMatch ? headMatch[2] : null;
      if (existingVersion === CLI_VERSION) {
        return { status: "skipped", path: filePath, reason: `already at v${CLI_VERSION}` };
      }
      // Replace the existing section in place.
      const next = existing.replace(sectionRe, section);
      mkdirSync(dirname(filePath), { recursive: true });
      writeFileSync(filePath, next);
      return existingVersion
        ? { status: "updated", path: filePath, previousVersion: existingVersion }
        : { status: "installed", path: filePath };
    }
  }

  // No existing section — append (creating the file if needed).
  mkdirSync(dirname(filePath), { recursive: true });
  const prefix = existing && !existing.endsWith("\n") ? "\n" : "";
  writeFileSync(filePath, existing + prefix + section);
  return { status: "installed", path: filePath };
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Remove a skill installation from an agent's target. Returns true if
 * something was removed.
 */
export function removeSkill(
  skillName: string,
  agent: DetectedAgent,
  cwd: string,
): boolean {
  if (agent.agent === "aider") {
    const filePath = join(cwd, agent.target);
    if (!existsSync(filePath)) return false;
    const content = readFileSync(filePath, "utf8");
    const startMarker = `<!-- clawnify-skill-start: ${skillName} -->`;
    const endMarker = `<!-- clawnify-skill-end: ${skillName} -->`;
    const sectionRe = new RegExp(
      `${escapeRe(startMarker)}[\\s\\S]*?${escapeRe(endMarker)}\\n?`,
    );
    if (!sectionRe.test(content)) return false;
    writeFileSync(filePath, content.replace(sectionRe, ""));
    return true;
  }

  if (agent.agent === "cursor") {
    const candidate = join(cwd, agent.target, `${skillName}.mdc`);
    if (!existsSync(candidate)) return false;
    unlinkSync(candidate);
    return true;
  }

  // Directory-based agents (claude, codex, openclaw): blow away the whole
  // skill dir. Small contents (SKILL.md + maybe skill.json), safe to rm -r.
  const dir = join(cwd, agent.target, skillName);
  if (!existsSync(dir)) return false;
  try {
    const s = statSync(dir);
    if (!s.isDirectory()) return false;
  } catch {
    return false;
  }
  rmSync(dir, { recursive: true, force: true });
  return true;
}
